import { Component, effect, input } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { BoostrapDropdownComponent } from '@shared/components/boostrap-dropdown/boostrap-dropdown.component';
import { DynamicField } from '@shared/components/dynamic-form/models/field';
import { ChoiceValue } from '@shared/components/dynamic-form/models/field-dynamic-choices';
import { SharedModule } from '@shared/shared.module';

@Component({
  selector: 'app-dynamic-choices-field',
  imports: [
    ReactiveFormsModule,
    SharedModule,
    BoostrapDropdownComponent,
    TranslateModule,
  ],
  templateUrl: './dynamic-choices-field.component.html',
})
export class DynamicChoicesFieldComponent {
  field = input.required<DynamicField>();
  form = input.required<FormGroup>();

  currentIndex = 0;

  choices = Array.from({ length: 9 }, (_, i) => ({
    codeId: i + 2,
    value: String(i + 2),
  }));

  get choicesFormArray() {
    return this.form().get('selections') as FormArray;
  }

  private readonly fb = new FormBuilder();

  constructor() {
    effect(() => {
      this.updateFormValue();
    });
  }

  onChangeChoice(item: { value: string }) {
    this.form().get('choices')?.setValue(item.value);
    this.updateChoicesArray(+item.value);
    this.updateCurrentIndex(item.value);
  }

  isDuplicateChoice(index: number): boolean {
    const choicesFormArray = this.choicesFormArray;
    if (!choicesFormArray.errors?.duplicateChoices) return false;

    const currentValue = choicesFormArray
      .at(index)
      .get('label')
      ?.value?.trim()
      .toLowerCase();
    if (!currentValue) return false;

    const values = choicesFormArray.controls.map(c =>
      c.get('label')?.value?.trim().toLowerCase()
    );
    return values.indexOf(currentValue) !== values.lastIndexOf(currentValue);
  }

  private updateCurrentIndex(value: string | number) {
    this.currentIndex = this.choices.findIndex(
      option => option.value === value.toString()
    );
  }

  private updateChoicesArray(count: number) {
    const choicesFormArray = this.choicesFormArray;

    while (choicesFormArray.length > count) {
      choicesFormArray.removeAt(choicesFormArray.length - 1);
    }

    while (choicesFormArray.length < count) {
      const index = choicesFormArray.length;
      choicesFormArray.push(
        this.fb.group({
          score: [index + 1],
          label: ['', [Validators.required]],
        })
      );
    }

    choicesFormArray.setValidators([this.duplicateChoicesValidator]);
    choicesFormArray.updateValueAndValidity();
  }

  private updateFormValue() {
    const fieldValue = this.field().value as ChoiceValue;
    const choices =
      fieldValue?.choices || this.form().get('choices')?.value || 2;

    let selections = new Array(choices).fill(null).map((_, index) => ({
      score: index + 1,
      label: '',
    }));

    if (
      fieldValue &&
      'selections' in fieldValue &&
      Array.isArray(fieldValue.selections)
    ) {
      selections = fieldValue.selections;
    }

    this.updateChoicesArray(+choices);
    this.updateCurrentIndex(choices);
    this.form().get('choices')?.setValue(+choices);
    this.choicesFormArray.setValue(selections);
  }

  private duplicateChoicesValidator(
    control: AbstractControl
  ): ValidationErrors | null {
    const formArray = control as FormArray;
    const values = formArray.controls.map(c =>
      c.get('label')?.value?.trim().toLowerCase()
    );

    const duplicates = values.filter(
      (value, index) => value && values.indexOf(value) !== index
    );

    return duplicates.length > 0 ? { duplicateChoices: true } : null;
  }
}
